import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import {
  andAllWhere,
  andWhere,
  buildQuery,
  CombinedFilter,
  FilterFn,
  noop,
  Ops,
  orderBy,
  OrderVariant,
  orWhere,
  page,
  select,
  where,
} from "@albatrosdeveloper/ave-utils-npm/lib/utils/query.util";
import { HttpClient } from "@angular/common/http";
import {
  IPagination,
  IPaginationResult,
  IPaginationResultFrequencies,
  IRequestResult,
} from "../../../../common/interfaces";
import { environment as env } from "../../../../../environments/environment.prod";
import { StatusEnum } from "@albatrosdeveloper/ave-models-package/lib/enums";
import { AbstractService } from "../../../../services/abstract.service";
import { BusinessPartnerTypeEnum } from "../../../../shared/components/business-partner-display/business-partner-type.enum";
import BusinessPartnerAttributes from "@albatrosdeveloper/ave-models-package/lib/schemas/business-partners/business-partner/business-partner.entity";
import BusinessPartnerGeoAttributes from "@albatrosdeveloper/ave-models-package/lib/schemas/business-partners/business-partner-geo/business-partner-geo.entity";
import { ICreateBusinessPartner } from "./interfaces/create-business-partner.interface";
import { IUpdateBusinessPartner } from "./interfaces/update-business-partner.interface";
import { RequestOptions } from "app/common/interfaces/options.interface";
import { BusinessPartnerBySubRoute } from "../../routing/sub-routes/interfaces/busines-partner-by-sub-route.interface";
import { IFilters } from "../../filters/interfaces/filterInterface";
import { BusinessPartnerWithRoutesResponse } from "../../routing/sub-routes/client-route-locator/interfaces/client-route-locator.interface";
import { ResponseBusinessPartner } from "../../modals/dto/routes-detail-by-businesss-partnert-vehicle-dto";

const apiUrl = env.API_BUSINESS_PARTNER;
const moduleName = "business-partner";

export const BUSINESS_PARTNER_IMPORT_ADDRESS_LINK = apiUrl + `api/${moduleName}/upload/customer_location`;
@Injectable({
  providedIn: "root",
})
export class BusinessPartnerService extends AbstractService<
  BusinessPartnerAttributes,
  ICreateBusinessPartner,
  IUpdateBusinessPartner
> {
  /** The last filter used to load the list */
  currentFilter: CombinedFilter<BusinessPartnerAttributes>;
  /** The last filter used to load the list */
  currentFilterTwo: CombinedFilter<BusinessPartnerAttributes>;
  /** The  filter used to load the list all addresses */
  currentFilterAllAddresses: CombinedFilter<BusinessPartnerAttributes>;

  currentGeoFilter: CombinedFilter<BusinessPartnerGeoAttributes>;

  constructor(http: HttpClient) {
    super(apiUrl, moduleName, http);
  }

  getWithTripsInfo(
    options: IPagination<any>,
    businessPartnerIds: string[]
  ): Observable<ResponseBusinessPartner> {

    const API_URL: string =
      apiUrl +
      `api/${moduleName}/findWithTripsInfo?businessPartnerIds=${businessPartnerIds}`;
    return this.http.get<ResponseBusinessPartner>(API_URL);
  }

  findById(id: string) {
    const API_URL: string = apiUrl + `api/${moduleName}/byId/${id}`;
    return this.http.get<IRequestResult<BusinessPartnerAttributes>>(API_URL);
  }

  findByFilter(
    filter: CombinedFilter<BusinessPartnerAttributes>,
    geolocationPoint: string,
    options?: RequestOptions
  ) {
    const geoParam =
      geolocationPoint === "both"
        ? ""
        : `&geo=${geolocationPoint === "valid" ? "true" : "false"}`;

    const API_URL: string =
      apiUrl +
      `api/${moduleName}/withCount?filter=${encodeURIComponent(JSON.stringify(filter))}&${geoParam}`;
    const headers: Record<string, any> = {};
    // see order-customer-export for more info about this header
    // ctx: stands for context
    if (options?.contextHeader) headers["x-ave-ctx"] = options.contextHeader;
    return this.http.get<IPaginationResult<BusinessPartnerAttributes>>(
      API_URL,
      { headers }
    );
  }

  findByFilterTwo(
    filter: CombinedFilter<BusinessPartnerAttributes>,
    options?: RequestOptions,
    idSubRoutePeriodicity?: string,
    type?: boolean
  ) {
    const API_URL: string =
      apiUrl +
      `api/${moduleName}/bySubRoute/${idSubRoutePeriodicity}?filter=${encodeURIComponent(JSON.stringify(filter))}&assign=${type}`;
    const headers: Record<string, any> = {};
    // see order-customer-export for more info about this header
    // ctx: stands for context
    if (options?.contextHeader) headers["x-ave-ctx"] = options.contextHeader;
    return this.http.get<IPaginationResult<BusinessPartnerAttributes>>(
      API_URL,
      { headers }
    );
  }

  /**
   * Obtiene socios de negocio con paginación y filtros.
   *
   * Estrategia de búsqueda:
   * - Sin búsqueda: Un solo objeto con filtros base
   * - Con búsqueda: Cuatro objetos (OR lógico) - busca en 'name', 'externalKeys.code', 'types.name', 'businessPartnerGroup.name'
   *
   * Cada objeto del OR debe incluir TODAS las condiciones base para mantener consistencia.
   *
   * @param options Pagination options
   * @param flagBusinessPartner Flag for business partner type filtering
   * @param newBusinessPartnerFlags New business partner flags
   * @param geolocationPoint Geolocation point filter
   * @param dataToFilter Additional filters
   * @param onlyCustomers Only customers flag
   * @returns Paginated data
   *
   * Search fields: name, externalKeys.code, types.name, businessPartnerGroup.name
   * Array filters: typeCustomer, newBusinessPartnerFlags, status, routeId
   * Single filters: companyId, startDate/endDate, flagBusinessPartner, onlyCustomers, businessPartnerRolId
   */
  findAllWithCount(
    options: IPagination<BusinessPartnerAttributes>,
    flagBusinessPartner: boolean,
    newBusinessPartnerFlags: number[],
    geolocationPoint: string,
    dataToFilter?: IFilters,
    onlyCustomers?: boolean
  ): Observable<IPaginationResult<BusinessPartnerAttributes>> {

    const hasSearch = !!options.search;

    /**
     * Construye las condiciones base que SIEMPRE deben aplicarse.
     * Incluye: _deleted=false, y todos los filtros condicionales.
     *
     * @param useOrWhere - Si es true, inicia un nuevo objeto con orWhere (para el OR lógico)
     */
    const buildBaseFilters = (params: { useOrWhere: boolean }): FilterFn<BusinessPartnerAttributes>[] => {
      const deletedFilter: FilterFn<BusinessPartnerAttributes> = params.useOrWhere
        ? orWhere('_deleted', Ops.eq(false))  // Inicia nuevo objeto en el array
        : where('_deleted', Ops.eq(false));   // Primer objeto del array

      const filters: FilterFn<BusinessPartnerAttributes>[] = [deletedFilter];

      // Filtro de tipo de cliente
      if (dataToFilter?.typeCustomer && dataToFilter.typeCustomer.length > 0) {
        filters.push(andWhere("types.code" as keyof BusinessPartnerAttributes, Ops.in(...dataToFilter.typeCustomer)));
      }

      // Filtro de flag business partner
      if (flagBusinessPartner) {
        filters.push(andWhere("types.code" as keyof BusinessPartnerAttributes, Ops.in(...["BPT0004"])));
      } else {
        filters.push(andWhere("types.code" as keyof BusinessPartnerAttributes, Ops.notIn(...["BPT0004"])));
      }

      // Filtro de nuevos socios de negocio
      if (newBusinessPartnerFlags && newBusinessPartnerFlags.length > 0) {
        filters.push(andWhere("newBusinessPartnerFlag" as keyof BusinessPartnerAttributes, Ops.in(...newBusinessPartnerFlags)));
      }

      // Filtro de estados
      if (dataToFilter?.status && dataToFilter.status.length > 0 && !dataToFilter.status.includes(null)) {
        filters.push(andWhere('statusId', Ops.in(...dataToFilter.status)));
      }

      // Filtro por territorios
      if (dataToFilter?.routeId && dataToFilter.routeId.length > 0 && !dataToFilter.routeId.includes(null)) {
        filters.push(andWhere('routes.id' as keyof BusinessPartnerAttributes, Ops.in(...dataToFilter.routeId)));
      }

      // Filtro de grupos de socio
      if (dataToFilter?.businessPartnerGroupId && dataToFilter.businessPartnerGroupId.length > 0 && !dataToFilter.businessPartnerGroupId.includes(null)) {
        filters.push(andWhere('businessPartnerGroup.id' as keyof BusinessPartnerAttributes, Ops.in(...dataToFilter.businessPartnerGroupId)));
      }

       // Filtro por ruta
      if (dataToFilter?.subRouteId && dataToFilter.subRouteId.length > 0 && !dataToFilter.subRouteId.includes(null)) {
        filters.push(andWhere('subRouteThereAreFrequencies.id' as keyof BusinessPartnerAttributes, Ops.in(...dataToFilter.subRouteId)));
      }

      // Filtro por pais
      if (dataToFilter?.locationOneIds && dataToFilter.locationOneIds.length > 0 && !dataToFilter.locationOneIds.includes(null)) {
        filters.push(andWhere('addresses.locationOne.code' as keyof BusinessPartnerAttributes, Ops.in(...dataToFilter.locationOneIds)));
      }

        // Filtro por departamento
      if (dataToFilter?.locationTwoIds && dataToFilter.locationTwoIds.length > 0 && !dataToFilter.locationTwoIds.includes(null)) {
        filters.push(andWhere('addresses.locationTwo.code' as keyof BusinessPartnerAttributes, Ops.in(...dataToFilter.locationTwoIds)));
      }
      
        // Filtro por ciudad
      if (dataToFilter?.locationThreeIds && dataToFilter.locationThreeIds.length > 0 && !dataToFilter.locationThreeIds.includes(null)) {
        filters.push(andWhere('addresses.locationThree.code' as keyof BusinessPartnerAttributes, Ops.in(...dataToFilter.locationThreeIds)));
      }

      // Filtro solo clientes
      if (onlyCustomers) {
        filters.push(andWhere("types.code" as keyof BusinessPartnerAttributes, Ops.in(...["BPT0001"])));
      }

      // Filtro de roles de socios de negocio
      if (dataToFilter?.businessPartnerRolId && dataToFilter.businessPartnerRolId.length > 0 && !dataToFilter.businessPartnerRolId.includes(null)) {
        const selectedRoles = dataToFilter.businessPartnerRolId;

        if (selectedRoles.includes('Em') && !selectedRoles.includes('Ex')) {
          filters.push(andWhere('types.isEmployee' as keyof BusinessPartnerAttributes, Ops.eq(true)));
        } else if (selectedRoles.includes('Ex') && !selectedRoles.includes('Em')) {
          filters.push(andWhere('types.isEmployee' as keyof BusinessPartnerAttributes, Ops.eq(false)));
        }
      }

      // Filtro de compañía
      if (dataToFilter?.companyId) {
        filters.push(andWhere("companies.id" as keyof BusinessPartnerAttributes, Ops.eq(dataToFilter.companyId)));
      }

      // Filtro de fechas
      if (dataToFilter?.startDate && dataToFilter?.endDate) {
        filters.push(andWhere("createdAt" as keyof BusinessPartnerAttributes, Ops.btwnDates(dataToFilter.startDate, dataToFilter.endDate)));
      }

      // Filtro por id de socio
      if (dataToFilter.customerId && dataToFilter.customerId.length>0) {
        filters.push(andWhere("id", Ops.in(...dataToFilter.customerId)));
      }

      return filters;
    };

    // ========== CONSTRUCCIÓN DE OBJETOS JSON ==========

    // Objeto 1: Condiciones base + búsqueda por NAME (o solo condiciones si no hay búsqueda)
    const searchByNameQueryObject: FilterFn<BusinessPartnerAttributes>[] = [
      ...buildBaseFilters({ useOrWhere: false }),  // false = usa 'where' (primer objeto)
      hasSearch ? andWhere("name", Ops.like(options.search!, "i")) : noop()
    ];

    // Objeto 2: Condiciones base + búsqueda por EXTERNAL KEYS CODE (solo si hay búsqueda)
    const searchByExternalCodeQueryObject: FilterFn<BusinessPartnerAttributes>[] = hasSearch ? [
      ...buildBaseFilters({ useOrWhere: true }),   // true = usa 'orWhere' (segundo objeto)
      andWhere("externalKeys.code" as keyof BusinessPartnerAttributes, Ops.like(options.search!, "i"))
    ] : [];

    // Objeto 3: Condiciones base + búsqueda por TYPES NAME (solo si hay búsqueda)
    const searchByTypesNameQueryObject: FilterFn<BusinessPartnerAttributes>[] = hasSearch ? [
      ...buildBaseFilters({ useOrWhere: true }),
      andWhere("types.name" as keyof BusinessPartnerAttributes, Ops.like(options.search!, "i"))
    ] : [];

    // Objeto 4: Condiciones base + búsqueda por BUSINESS PARTNER GROUP NAME (solo si hay búsqueda)
    const searchByGroupNameQueryObject: FilterFn<BusinessPartnerAttributes>[] = hasSearch ? [
      ...buildBaseFilters({ useOrWhere: true }),
      andWhere("businessPartnerGroup.name" as keyof BusinessPartnerAttributes, Ops.like(options.search!, "i"))
    ] : [];

    // Objeto 4: Condiciones base + búsqueda por COMERCIAL NAME (solo si hay búsqueda)
    const searchByCommercialNameQueryObject: FilterFn<BusinessPartnerAttributes>[] = hasSearch ? [
      ...buildBaseFilters({ useOrWhere: true }),
      andWhere("commercialName" as keyof BusinessPartnerAttributes, Ops.like(options.search!, "i"))
    ] : [];

    // Objeto 5: Condiciones base + búsqueda por NIT (solo si hay búsqueda)
    const searchByNITNameQueryObject: FilterFn<BusinessPartnerAttributes>[] = hasSearch ? [
      ...buildBaseFilters({ useOrWhere: true }),
      andWhere("NIT" as keyof BusinessPartnerAttributes, Ops.like(options.search!, "i"))
    ] : [];

    // Objeto 4: Condiciones base + búsqueda por DNI (solo si hay búsqueda)
    const searchByDNIQueryObject: FilterFn<BusinessPartnerAttributes>[] = hasSearch ? [
      ...buildBaseFilters({ useOrWhere: true }),
      andWhere("DNI" as keyof BusinessPartnerAttributes, Ops.like(options.search!, "i"))
    ] : [];

    // ========== ENSAMBLAJE FINAL ==========

    const query = buildQuery<BusinessPartnerAttributes>(
      // Expandimos los cuatro objetos en el buildQuery
      ...searchByNameQueryObject,
      ...searchByExternalCodeQueryObject,
      ...searchByTypesNameQueryObject,
      ...searchByGroupNameQueryObject,
      ...searchByCommercialNameQueryObject,
      ...searchByNITNameQueryObject,
      ...searchByDNIQueryObject,

      // Ordenamiento y paginación
      orderBy(
        options.sort ?? "name",
        options.sortDirection === "ASC" ? OrderVariant.Asc : OrderVariant.Desc
      ),
      page(options.currentPage ?? 1, options.limitPerPage ?? 100)
    );

    this.currentFilter = query;

    const geoParam =
      geolocationPoint === "both"
        ? ""
        : `&geo=${geolocationPoint === "valid" ? "true" : "false"}`;

    const API_URL: string =
      apiUrl +
      `api/${moduleName}/withCount?filter=${encodeURIComponent(JSON.stringify(query))}&${geoParam}`;
    return this.http.get<IPaginationResult<BusinessPartnerAttributes>>(API_URL);
  }

  /**
   * Pagination
   * @param options Pagination options
   * @returns Paginated data
   */
  findAllWithCountBySubRoute(
    options: IPagination<BusinessPartnerAttributes>,
    subRouteId: string,
    type: boolean //Asignado o No asignado
  ): Observable<IPaginationResultFrequencies<BusinessPartnerBySubRoute>> {
    const query = buildQuery<BusinessPartnerAttributes>(
      where("name", Ops.like(options.search ?? "", "i")),
      orWhere(
        "externalKeys.code" as keyof BusinessPartnerAttributes,
        Ops.like(options.search ?? "", "i")
      ),
      orWhere(
        "contacts.contact.fullName" as keyof BusinessPartnerAttributes,
        Ops.like(options.search ?? "", "i")
      ),
      orWhere(
        "addresses.address" as keyof BusinessPartnerAttributes,
        Ops.like(options.search ?? "", "i")
      ),
      orWhere(
        "addresses.locationTwo.name" as keyof BusinessPartnerAttributes,
        Ops.like(options.search ?? "", "i")
      ),
      orWhere(
        "addresses.locationThree.name" as keyof BusinessPartnerAttributes,
        Ops.like(options.search ?? "", "i")
      ),
      orWhere(
        "addresses.locationFour.name" as keyof BusinessPartnerAttributes,
        Ops.like(options.search ?? "", "i")
      ),
      andAllWhere(
        "types.code" as keyof BusinessPartnerAttributes,
        Ops.notIn(...["BPT0004"])
      ),
      andAllWhere("_deleted", Ops.eq(false)),
      // andAllWhere('subRouteIdIds' as keyof BusinessPartnerAttributes, Ops.eq(subRouteId)),

      orderBy(
        options.sort ?? "name",
        options.sortDirection == "ASC" ? OrderVariant.Asc : OrderVariant.Desc
      ),
      page(options.currentPage ?? 1, options.limitPerPage ?? 100)
    );

    this.currentFilterTwo = query;
    const API_URL: string =
      apiUrl +
      `api/${moduleName}/bySubRoute/${subRouteId}?filter=${encodeURIComponent(JSON.stringify(query))}&assign=${type}`;
    return this.http.get<
      IPaginationResultFrequencies<BusinessPartnerBySubRoute>
    >(API_URL);
  }

  /**
   * Find to combo box
   * @returns Data
   */
  findToComboBox(
    typesCodes: string[]
  ): Observable<IRequestResult<BusinessPartnerAttributes[]>> {
    let arrayQueries: any[] = [];

    if (typesCodes.length > 0) {
      arrayQueries.push(andAllWhere("types.code", Ops.in(...typesCodes)));
    }

    const query = buildQuery<BusinessPartnerAttributes>(
      where("statusId", StatusEnum.ACTIVE),
      andAllWhere(
        "types.code" as keyof BusinessPartnerAttributes,
        Ops.notIn(...["BPT0004"])
      ),
      ...arrayQueries,
      select(["id", "name", "externalKeys"]),
      orderBy("id", OrderVariant.Asc)
    );

    const API_URL: string =
      apiUrl +
      `api/${moduleName}/filter?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IRequestResult<BusinessPartnerAttributes[]>>(API_URL);
  }

  findToComboBoxCustomer(
    options: IPagination<BusinessPartnerAttributes>,
    companyIds?: number[],
    routesIds?: string[],
    groupIds?: string[],
  ): Observable<IRequestResult<BusinessPartnerAttributes[]>> {
    const query = buildQuery<BusinessPartnerAttributes>(
      where("statusId", StatusEnum.ACTIVE),
      andWhere("name", Ops.like(options.search ?? "", "i")),
      orWhere(<any>"externalKeys.code", Ops.like(options.search ?? "", "i")),
      andAllWhere(
        "types.code" as keyof BusinessPartnerAttributes,
        Ops.in(...["BPT0001"])
      ),
      companyIds && companyIds.length > 0
        ? andAllWhere(
          "companies._id" as keyof BusinessPartnerAttributes,
          Ops.in(...companyIds)
        )
        : noop(),
      routesIds && routesIds.length > 0
        ? andAllWhere(
          "routes._id" as keyof BusinessPartnerAttributes,
          Ops.in(...routesIds)
        )
        : noop(),
      groupIds && groupIds.length > 0
        ? andAllWhere(
          "businessPartnerGroup.id" as keyof BusinessPartnerAttributes,
          Ops.in(...groupIds)
        ) : noop(),
      select(["id", "name", "externalKeys", "_id"]),
      orderBy("id", OrderVariant.Asc),
      page(options.currentPage ?? 1, options.limitPerPage ?? 100)
    );

    const API_URL: string =
      apiUrl +
      `api/${moduleName}/filter?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IRequestResult<BusinessPartnerAttributes[]>>(API_URL);
  }

  findToComboBoxMultipleBP(
    options: IPagination<BusinessPartnerAttributes>,
    businessPartnerIds: string[],
    companyIds: number[],
    businessPartnerGroup?: {
      groupIds: string[],
      businessGroupWithMongoId: boolean
    }
  ): Observable<IRequestResult<BusinessPartnerAttributes[]>> {
    const groupKey = businessPartnerGroup?.businessGroupWithMongoId ? "businessPartnerGroup._id" : "businessPartnerGroup.id";

    const query = buildQuery<BusinessPartnerAttributes>(
      where("statusId", StatusEnum.ACTIVE),
      andWhere("name", Ops.like(options.search ?? "", "i")),
      orWhere(<any>"externalKeys.code", Ops.like(options.search ?? "", "i")),
      andAllWhere(
        "types.code" as keyof BusinessPartnerAttributes,
        Ops.in(...["BPT0001"])
      ),
      companyIds && companyIds.length > 0
        ? andAllWhere(
          "companies._id" as keyof BusinessPartnerAttributes,
          Ops.in(...companyIds)
        )
        : noop(),
      businessPartnerGroup?.groupIds && businessPartnerGroup.groupIds.length > 0
        ? andAllWhere(
          groupKey as keyof BusinessPartnerAttributes,
          Ops.in(...businessPartnerGroup.groupIds)
        ) : noop(),
      businessPartnerIds && businessPartnerIds.length > 0 ? andAllWhere(
        "_id" as keyof BusinessPartnerAttributes,
        Ops.in(...businessPartnerIds)
      ) : noop(),
      orderBy("id", OrderVariant.Asc),
      page(options.currentPage ?? 1, options.limitPerPage ?? 100)
    );

    const API_URL: string =
      apiUrl +
      `api/${moduleName}/filter?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IRequestResult<BusinessPartnerAttributes[]>>(API_URL);
  }

  approveRejectBussinessPartner(data) {
    const API_URL: string = apiUrl + `api/${moduleName}/approve`;
    return this.http.post<any>(API_URL, data);
  }

  findToComboDeliveryRoutes(
    options: IPagination<BusinessPartnerAttributes>,
    shipToIds: number[]
  ): Observable<IRequestResult<BusinessPartnerAttributes[]>> {
    const query = buildQuery<BusinessPartnerAttributes>(
      where("statusId", StatusEnum.ACTIVE),
      andAllWhere(
        "types.code" as keyof BusinessPartnerAttributes,
        Ops.in(...["BPT0001", "BPT9001", "BPT9002"]) // Cliente normal, consumidor final y cliente eventual
      ),
      shipToIds && shipToIds.length > 0
        ? andAllWhere(
          "id" as keyof BusinessPartnerAttributes,
          Ops.in(...shipToIds)
        )
        : noop(),
      select(["id", "name", "externalKeys", "routes"]),
      orderBy("id", OrderVariant.Asc),
      page(options.currentPage ?? 1, options.limitPerPage ?? 100)
    );

    const API_URL: string =
      apiUrl +
      `api/${moduleName}/with_delivery_subroutes?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IRequestResult<BusinessPartnerAttributes[]>>(API_URL);
  }

  findToComboBusinessGroup(
    options: IPagination<BusinessPartnerAttributes>,
    businessIds: string[]
  ): Observable<IRequestResult<BusinessPartnerAttributes[]>> {
    const query = buildQuery<BusinessPartnerAttributes>(
      where("_id" as keyof BusinessPartnerAttributes, Ops.in(...businessIds)),
      select(["id", "name", "externalKeys"]),
      orderBy("id", OrderVariant.Asc),
      page(options.currentPage ?? 1, options.limitPerPage ?? 200)
    );

    const API_URL: string =
      apiUrl +
      `api/${moduleName}/with_delivery_subroutes?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IRequestResult<BusinessPartnerAttributes[]>>(API_URL);
  }

  saveFileBusinessPartnerFile(file: File, optionLoad: string) {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("transform", optionLoad);
    const API_URL: string = apiUrl + `api/${moduleName}/upload/presales/v2`;
    return this.http.post<IRequestResult<BusinessPartnerAttributes>>(
      API_URL,
      formData
    );
  }

  getBusinessPartnerExcelData() {
    return env.BUSINESS_PARTNER_DATA;
  }

  saveFileBusinessPartnerClientFile(file: File, optionLoad: string) {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("transform", optionLoad);
    const API_URL: string = apiUrl + `api/${moduleName}/upload/customer/v2`;
    return this.http.post<IRequestResult<BusinessPartnerAttributes>>(
      API_URL,
      formData
    );
  }

  getBusinessPartnerClientExcelData() {
    return env.BUSINESS_PARTNER_CLIENT_DATA;
  }

  saveImportAddressFile(data: unknown) {
    const API_URL: string = apiUrl + `api/${moduleName}/upload/customer_location`;
    return this.http.post<IRequestResult<BusinessPartnerAttributes>>(API_URL, data);
  }


  /**
  * Pagination
  * @param options Pagination options
  * @returns Paginated data
  */
  findAllWithCountByGeoUpdate(
    options: IPagination<BusinessPartnerGeoAttributes>,
    dataToFilter?,
  ): Observable<IPaginationResult<BusinessPartnerGeoAttributes>> {
    let arrayQueries: any[] = [];

    if (dataToFilter.typesCodes.length > 0) {
      arrayQueries.push(andAllWhere("types.code", Ops.in(...dataToFilter.typesCodes)));
    }

    if (dataToFilter.flagBusinessPartner) {
      arrayQueries.push(andAllWhere("types.code", Ops.in(...["BPT0004"])));
      //arrayQueries.push(andAllWhere("newBusinessPartnerFlag", Ops.in(...[StatusBusinessPartnerEnum.IN_PROCESS, StatusBusinessPartnerEnum.PENDING, StatusBusinessPartnerEnum.REJECTED])))
    } else {
      arrayQueries.push(andAllWhere("types.code", Ops.notIn(...["BPT0004"])));
      //arrayQueries.push(andAllWhere("newBusinessPartnerFlag", Ops.notIn(...[StatusBusinessPartnerEnum.IN_PROCESS, StatusBusinessPartnerEnum.PENDING, StatusBusinessPartnerEnum.REJECTED])))
    }

    if (dataToFilter.routesId && dataToFilter.routesId.length > 0) arrayQueries.push(andAllWhere("subRouteGeoUpdatePending.id", Ops.in(...dataToFilter.routesId)))

    if (dataToFilter.allStatus) {
      arrayQueries.push(
        andAllWhere("addresses.geolocationPointUpdate", Ops.eq(dataToFilter.status))
      );
    }


    const query = buildQuery<BusinessPartnerGeoAttributes>(
      where("_deleted", Ops.eq(false)),
      andWhere("name", Ops.like(options.search ?? "", "i")),
      orWhere(<any>"externalKeys.code", Ops.like(options.search ?? "", "i")),
      orWhere(<any>"types.name", Ops.like(options.search ?? "", "i")),

      dataToFilter.start && dataToFilter.end
        ? andAllWhere("createdAt", Ops.btwnDates(dataToFilter.start, dataToFilter.end))
        : noop(),
      ...arrayQueries,
      orderBy(
        options.sort ?? "name",
        options.sortDirection == "ASC" ? OrderVariant.Asc : OrderVariant.Desc
      ),
      page(options.currentPage ?? 1, options.limitPerPage ?? 100)
    );

    this.currentGeoFilter = query;

    //TODO ADD QUERYPARAMS TO GEO
    const API_URL: string =
      apiUrl +
      `api/${moduleName}/findAllWithCountByGeoUpdate?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IPaginationResult<BusinessPartnerGeoAttributes>>(API_URL);
  }

  /**
 * Pagination
 * @param options Pagination options
 * @returns Paginated data
 */
  findAllForImportGeo(
    options: IPagination<BusinessPartnerAttributes>,
    routesIds: number[],
    routeAreaIds: number[],
    locationTwoIds: string[],
    subRouteIds: number[],
    businessIds: number[]
  ): Observable<IPaginationResult<BusinessPartnerAttributes>> {

    let arrayQueries: any[] = [];


    if (routesIds.length > 0) {
      arrayQueries.push(andAllWhere("routes.id", Ops.in(...routesIds)))
    }

    if (routeAreaIds.length > 0) {
      arrayQueries.push(andAllWhere("routeArea.id", Ops.in(...routeAreaIds)))
    }

    if (locationTwoIds.length > 0) {
      arrayQueries.push(andAllWhere("addresses.locationTwo.code", Ops.in(...locationTwoIds)))
    }

    if (subRouteIds.length > 0) {
      arrayQueries.push(andAllWhere("subRouteThereAreFrequencies.id", Ops.in(...subRouteIds)))
    }

    if (businessIds.length > 0) {
      arrayQueries.push(andAllWhere("id", Ops.in(...businessIds)))
    }




    const query = buildQuery<BusinessPartnerAttributes>(
      where("_deleted", Ops.eq(false)),
      andWhere("name", Ops.like(options.search ?? "", "i")),
      orWhere(<any>"externalKeys.code", Ops.like(options.search ?? "", "i")),
      orWhere(<any>"types.name", Ops.like(options.search ?? "", "i")),
      // andAllWhere(<any>"subRouteThereAreFrequencies.id", Ops.gt(0)),
      ...arrayQueries,
      orderBy(
        options.sort ?? "name",
        options.sortDirection == "ASC" ? OrderVariant.Asc : OrderVariant.Desc
      ),
      page(options.currentPage ?? 1, options.limitPerPage ?? 100)
    );

    this.currentFilterAllAddresses = query;

    const API_URL: string =
      apiUrl +
      `api/${moduleName}/withCount?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IPaginationResult<BusinessPartnerAttributes>>(API_URL);
  }


  findByAllAddresses(
    filter: CombinedFilter<BusinessPartnerAttributes>,
    options?: RequestOptions
  ) {
    const API_URL: string =
      apiUrl +
      `api/${moduleName}/withCount?filter=${encodeURIComponent(JSON.stringify(filter))}`;
    const headers: Record<string, any> = {};
    // see order-customer-export for more info about this header
    // ctx: stands for context
    if (options?.contextHeader) headers["x-ave-ctx"] = options.contextHeader;
    return this.http.get<IPaginationResult<BusinessPartnerAttributes>>(
      API_URL,
      { headers }
    );
  }

  /**
 * Find to combo box
 * @returns Data
 */
  findToComboBoxByRoutes(
    id: string
  ): Observable<IRequestResult<BusinessPartnerAttributes[]>> {
    const query = buildQuery<BusinessPartnerAttributes>(
      where("statusId", StatusEnum.ACTIVE),
      id
        ? andAllWhere(
          "_id" as keyof BusinessPartnerAttributes,
          Ops.eq(id)
        )
        : noop(),
      select(["routes"]),
      orderBy("id", OrderVariant.Asc)
    );

    const API_URL: string =
      apiUrl +
      `api/${moduleName}/filter?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IRequestResult<BusinessPartnerAttributes[]>>(API_URL);
  }

  /**
   * Search business partners with their associated routes
   * @param searchTerm Search term for name or code
   * @param pageIndex Page index (0-based)
   * @param pageSize Page size
   * @returns Observable with business partners and their routes
   */
  searchBusinessPartnersWithRoutes(searchTerm?: string, pageIndex: number = 1, pageSize: number = 10): Observable<BusinessPartnerWithRoutesResponse> {
    const queryFilters: any[] = [
      where("_deleted", Ops.eq(false)),
      page(pageIndex + 1, pageSize)
    ];

    if (searchTerm && searchTerm.trim()) {
      queryFilters.push(
        andWhere("name", Ops.like(searchTerm.trim(), "i")),
        orWhere("externalKeys.code", Ops.like(searchTerm.trim(), "i")),
        orWhere("commercialName", Ops.like(searchTerm.trim(), "i")),
      );
    }

    const query = buildQuery<BusinessPartnerAttributes>(...queryFilters);

    const API_URL = `${apiUrl}api/${moduleName}/with_routes?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<BusinessPartnerWithRoutesResponse>(API_URL);
  }

  /**
   * Searches business partners eligible to be collaborators (excludes customers).
   * Filters out excluded types: CLIENTE (BPT0001), NUEVO_CLIENTE (BPT0004),
   * CONSUMIDOR_FINAL (BPT9001), CLIENTE_EVENTUAL (BPT9002).
   *
   * Estrategia de búsqueda:
   * - Sin búsqueda: Un solo objeto con filtros base
   * - Con búsqueda: Dos objetos (OR lógico) - busca en 'name' y 'externalKeys.code'
   *
   * @param searchTerm - Search term for name or code (optional)
   * @param pageSize - Number of results to return (default: 10)
   * @returns Observable with filtered business partners
   */
  searchCollaboratorCandidates(
    searchTerm?: string,
    pageSize: number = 10
  ): Observable<IRequestResult<BusinessPartnerAttributes[]>> {
    const hasSearch = searchTerm && searchTerm.trim();

    // Tipos excluidos: no pueden ser colaboradores
    const excludedTypes = [
      BusinessPartnerTypeEnum.CLIENTE,
      BusinessPartnerTypeEnum.NUEVO_CLIENTE,
      BusinessPartnerTypeEnum.CONSUMIDOR_FINAL,
      BusinessPartnerTypeEnum.CLIENTE_EVENTUAL
    ];

    /**
     * Construye las condiciones base que SIEMPRE deben aplicarse.
     * Incluye: _deleted=false, statusId=ACTIVE, tipos excluidos.
     *
     * @param useOrWhere - Si es true, inicia un nuevo objeto con orWhere (para el OR lógico)
     */
    const buildBaseFilters = (params: { useOrWhere: boolean }): FilterFn<BusinessPartnerAttributes>[] => {
      const deletedFilter: FilterFn<BusinessPartnerAttributes> = params.useOrWhere
        ? orWhere('_deleted', Ops.eq(false))  // Inicia nuevo objeto en el array
        : where('_deleted', Ops.eq(false));   // Primer objeto del array

      return [
        deletedFilter,
        andWhere('statusId', Ops.eq(StatusEnum.ACTIVE)),
        andWhere('types.code' as keyof BusinessPartnerAttributes, Ops.notIn(...excludedTypes))
      ];
    };

    // ========== CONSTRUCCIÓN DE OBJETOS JSON ==========

    // Objeto 1: Condiciones base + búsqueda por NAME (o solo condiciones si no hay búsqueda)
    const searchByNameQueryObject: FilterFn<BusinessPartnerAttributes>[] = [
      ...buildBaseFilters({ useOrWhere: false }),  // false = usa 'where' (primer objeto)
      hasSearch ? andWhere("name", Ops.like(searchTerm.trim(), "i")) : noop()
    ];

    // Objeto 2: Condiciones base + búsqueda por EXTERNAL KEYS CODE (solo si hay búsqueda)
    const searchByExternalCodeQueryObject: FilterFn<BusinessPartnerAttributes>[] = hasSearch ? [
      ...buildBaseFilters({ useOrWhere: true }),   // true = usa 'orWhere' (segundo objeto)
      andWhere("externalKeys.code" as keyof BusinessPartnerAttributes, Ops.like(searchTerm.trim(), "i"))
    ] : [];

    // ========== ENSAMBLAJE FINAL ==========

    const query = buildQuery<BusinessPartnerAttributes>(
      ...searchByNameQueryObject,
      ...searchByExternalCodeQueryObject,
      // Seleccionar solo los campos necesarios
      select(["id", "name", "externalKeys", "_id", "types"]),
      // Ordenar por ID
      orderBy("id", OrderVariant.Asc),
      // Paginación
      page(1, pageSize)
    );

    const API_URL: string =
      apiUrl +
      `api/${moduleName}/filter?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IRequestResult<BusinessPartnerAttributes[]>>(API_URL);
  }

  /**
   * Obtiene socios de negocio con paginación y filtros.
   *
   * @param options Pagination options
   * @param flagBusinessPartner Flag for business partner type filtering
   * @param newBusinessPartnerFlags New business partner flags
   * @param dataToFilter Additional filters
   * @returns Paginated data
   *
   * Array filters: typeCustomer, newBusinessPartnerFlags, status, routeId
   * Single filters: companyId, startDate/endDate, flagBusinessPartner, onlyCustomers, businessPartnerRolId
   */
  findAllWithCountNotBP(
    options: IPagination<BusinessPartnerAttributes>,
    flagBusinessPartner: boolean,
  ): Observable<IPaginationResultFrequencies<BusinessPartnerAttributes>> {

    const hasSearch = !!options.search;

    /**
     * Construye las condiciones base que SIEMPRE deben aplicarse.
     * Incluye: _deleted=false, y todos los filtros condicionales.
     *
     * @param useOrWhere - Si es true, inicia un nuevo objeto con orWhere (para el OR lógico)
     */
    const buildBaseFilters = (params: { useOrWhere: boolean }): FilterFn<BusinessPartnerAttributes>[] => {
      const deletedFilter: FilterFn<BusinessPartnerAttributes> = params.useOrWhere
        ? orWhere('_deleted', Ops.eq(false))  // Inicia nuevo objeto en el array
        : where('_deleted', Ops.eq(false));   // Primer objeto del array

      const filters: FilterFn<BusinessPartnerAttributes>[] = [deletedFilter];

      // Filtro de flag business partner
      if (flagBusinessPartner) {
        filters.push(andWhere("types.code" as keyof BusinessPartnerAttributes, Ops.in(...["BPT0004"])));
      } else {
        filters.push(andWhere("types.code" as keyof BusinessPartnerAttributes, Ops.notIn(...["BPT0004"])));
      }

      return filters;
    };

    // ========== CONSTRUCCIÓN DE OBJETOS JSON ==========

    // Objeto 1: Condiciones base + búsqueda por NAME (o solo condiciones si no hay búsqueda)
    const searchByNameQueryObject: FilterFn<BusinessPartnerAttributes>[] = [
      ...buildBaseFilters({ useOrWhere: false }),  // false = usa 'where' (primer objeto)
      hasSearch ? andWhere("name", Ops.like(options.search!, "i")) : noop()
    ];

    // Objeto 2: Condiciones base + búsqueda por EXTERNAL KEYS CODE (solo si hay búsqueda)
    const searchByExternalCodeQueryObject: FilterFn<BusinessPartnerAttributes>[] = hasSearch ? [
      ...buildBaseFilters({ useOrWhere: true }),   // true = usa 'orWhere' (segundo objeto)
      andWhere("externalKeys.code" as keyof BusinessPartnerAttributes, Ops.like(options.search!, "i"))
    ] : [];

    // ========== ENSAMBLAJE FINAL ==========

    const query = buildQuery<BusinessPartnerAttributes>(
      ...searchByNameQueryObject,
      ...searchByExternalCodeQueryObject,

      orderBy(
        options.sort ?? "name",
        options.sortDirection === "ASC" ? OrderVariant.Asc : OrderVariant.Desc
      ),
      page(options.currentPage ?? 1, options.limitPerPage ?? 100)
    );

    const API_URL: string =
      apiUrl +
      `api/${moduleName}/bps_not_related_to_users?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IPaginationResultFrequencies<BusinessPartnerAttributes>>(API_URL);
  }
}
